import type React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';

import { MAX_FILESTACK_FILE_SIZE } from '@aline/common';
import { Apollo } from 'src/gql/generated';
import {
  acceptedMimeTypePrefixes,
  acceptedMimeTypes,
  extractFileName,
  useFilestackClient,
} from 'src/helpers/integrations/filestack';
import { filestackS3StoreOptions } from 'src/helpers/integrations/filestack-multiupload';
import { parseFilestackUploadPayloads } from 'src/helpers/integrations/filestack-upload-payload';
import { useCreateFiles, useNotificationDispatch } from 'src/hooks';
import { NotificationsActionType } from 'src/notifications';
import { v4 as uuid4 } from 'uuid';

import { BLOCKED_SIGNING_DROP_MESSAGE } from './document-panel-messages';

interface UseDocumentDropzoneOptions {
  documentVariant: Apollo.DocumentVariant;
  workflowId: UUID;
  disabled?: boolean;
}

interface UseDocumentDropzoneReturn {
  dropzoneProps: {
    onDragEnter: (e: React.DragEvent) => void;
    onDragLeave: (e: React.DragEvent) => void;
    onDragOver: (e: React.DragEvent) => void;
    onDrop: (e: React.DragEvent) => void;
  };
  isDragging: boolean;
  isUploading: boolean;
}

function isFileAccepted(file: File) {
  if (acceptedMimeTypes.has(file.type)) return true;
  return acceptedMimeTypePrefixes.some(prefix => file.type.startsWith(prefix));
}

export function useDocumentDropzone(
  options: UseDocumentDropzoneOptions
): UseDocumentDropzoneReturn {
  const { disabled = false, documentVariant, workflowId } = options;
  const dragCounter = useRef(0);
  const dispatch = useNotificationDispatch();
  const isUploadingRef = useRef(false);
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const { createFiles } = useCreateFiles();
  const filestackClient = useFilestackClient();
  const [addDocumentsToWorkflow] = Apollo.useAddDocumentsToWorkflowMutation();

  const uploadFiles = useCallback(
    async (files: File[]) => {
      if (disabled || isUploadingRef.current) return;

      const validFiles = files.filter(
        file => file.size <= MAX_FILESTACK_FILE_SIZE && isFileAccepted(file)
      );
      const invalidFileCount = files.length - validFiles.length;
      if (invalidFileCount > 0) {
        dispatch({
          payload: {
            key: uuid4(),
            message: `${invalidFileCount} file${invalidFileCount > 1 ? 's were' : ' was'} skipped (unsupported format or too large).`,
            variant: 'warning',
          },
          type: NotificationsActionType.PUSH_NOTIFICATION,
        });
      }

      if (!validFiles.length) return;

      isUploadingRef.current = true;
      setIsUploading(true);

      try {
        const uploadResults = await filestackClient.multiupload(
          validFiles,
          {},
          filestackS3StoreOptions
        );
        const uploadPayloads = parseFilestackUploadPayloads(uploadResults, validFiles.length);
        const { data } = await createFiles({
          variables: {
            input: {
              records: uploadPayloads.map((result, i) => ({
                documentProps: {
                  isSigned: false,
                  variant: documentVariant,
                },
                fileLocationProps: {
                  container: result.container,
                  filename: extractFileName(validFiles[i].name),
                  handle: result.handle,
                  key: result.key,
                  mimetype: result.mimetype,
                  size: result.size,
                  url: result.url,
                },
              })),
            },
          },
        });

        const documentIds = data?.createFiles?.map(file => file.id) ?? [];
        if (documentIds.length) {
          await addDocumentsToWorkflow({
            variables: { input: { documentIds, workflowId } },
          });
        }

        dispatch({
          payload: {
            key: uuid4(),
            message: `${documentIds.length} document${documentIds.length > 1 ? 's' : ''} added to workflow.`,
            variant: 'success',
          },
          type: NotificationsActionType.PUSH_NOTIFICATION,
        });
      } catch (error) {
        dispatch({
          payload: {
            key: uuid4(),
            message: error instanceof Error ? error.message : 'Upload failed.',
            variant: 'error',
          },
          type: NotificationsActionType.PUSH_NOTIFICATION,
        });
      } finally {
        isUploadingRef.current = false;
        setIsUploading(false);
      }
    },
    [
      disabled,
      filestackClient,
      createFiles,
      addDocumentsToWorkflow,
      workflowId,
      documentVariant,
      dispatch,
    ]
  );

  const handleDragEnter = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (disabled) return;

      dragCounter.current += 1;
      if (e.dataTransfer?.items?.length) setIsDragging(true);
    },
    [disabled]
  );

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();

    dragCounter.current -= 1;
    if (dragCounter.current === 0) setIsDragging(false);
  }, []);

  const handleDragOver = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (disabled) return;

      e.dataTransfer.dropEffect = 'copy';
    },
    [disabled]
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();

      dragCounter.current = 0;
      setIsDragging(false);
      const files = Array.from(e.dataTransfer?.files ?? []);
      if (disabled && files.length > 0) {
        dispatch({
          payload: {
            key: uuid4(),
            message: BLOCKED_SIGNING_DROP_MESSAGE,
            variant: 'warning',
          },
          type: NotificationsActionType.PUSH_NOTIFICATION,
        });
        return;
      }
      uploadFiles(files);
    },
    [disabled, dispatch, uploadFiles]
  );

  useEffect(() => {
    const resetDragState = () => {
      dragCounter.current = 0;
      setIsDragging(false);
    };

    document.addEventListener('drop', resetDragState);
    return () => {
      document.removeEventListener('drop', resetDragState);
    };
  }, []);

  return {
    dropzoneProps: {
      onDragEnter: handleDragEnter,
      onDragLeave: handleDragLeave,
      onDragOver: handleDragOver,
      onDrop: handleDrop,
    },
    isDragging,
    isUploading,
  };
}
